import { useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';

/**
 * Custom hook for managing URL parameters with type safety
 * Provides utilities for getting, setting, and managing search parameters
 */
export const useUrlParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get a parameter value with optional default
  const getParam = useCallback((key: string, defaultValue?: string): string => {
    return searchParams.get(key) || defaultValue || '';
  }, [searchParams]);

  // Get a parameter as boolean
  const getBooleanParam = useCallback((key: string, defaultValue = false): boolean => {
    const value = searchParams.get(key);
    if (value === null) return defaultValue;
    return value === 'true' || value === '1';
  }, [searchParams]);

  // Get a parameter as number
  const getNumberParam = useCallback((key: string, defaultValue?: number): number | undefined => {
    const value = searchParams.get(key);
    if (value === null) return defaultValue;
    const num = parseInt(value, 10);
    return isNaN(num) ? defaultValue : num;
  }, [searchParams]);

  // Set a single parameter
  const setParam = useCallback((key: string, value: string | number | boolean | null) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (value === null || value === '' || value === undefined) {
      newParams.delete(key);
    } else {
      newParams.set(key, String(value));
    }
    
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Set multiple parameters at once
  const setParams = useCallback((params: Record<string, string | number | boolean | null>) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === '' || value === undefined) {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    });
    
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Clear all parameters
  const clearParams = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  // Clear specific parameters
  const clearParam = useCallback((key: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(key);
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Toggle a boolean parameter
  const toggleParam = useCallback((key: string) => {
    const current = getBooleanParam(key);
    setParam(key, !current);
  }, [getBooleanParam, setParam]);

  // Get all parameters as an object
  const getAllParams = useCallback((): Record<string, string> => {
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }, [searchParams]);

  // Build URL with current parameters
  const buildUrl = useCallback((basePath: string, additionalParams?: Record<string, string>): string => {
    const params = new URLSearchParams(searchParams);
    
    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      });
    }
    
    const search = params.toString();
    return basePath + (search ? `?${search}` : '');
  }, [searchParams]);

  return {
    // Getters
    getParam,
    getBooleanParam,
    getNumberParam,
    getAllParams,
    
    // Setters
    setParam,
    setParams,
    clearParams,
    clearParam,
    toggleParam,
    
    // Utilities
    buildUrl,
    searchParams,
  };
};

// Healer-specific parameter management
export const useHealerParams = () => {
  const urlParams = useUrlParams();

  return {
    ...urlParams,
    
    // Healer dashboard specific
    getTab: () => urlParams.getParam('tab', 'clients') as 'clients' | 'content' | 'appointments' | 'appointment-types' | 'software-waitlist',
    setTab: (tab: string) => {
      // Clear tab-specific filters when switching tabs
      urlParams.setParams({
        tab,
        clientStatus: null,
        appointmentStatus: null,
        search: null,
        page: null,
      });
    },
    
    // Client management
    getClientStatus: () => urlParams.getParam('clientStatus') as 'active' | 'inactive' | 'prospective' | 'archived' | '',
    setClientStatus: (status: string | null) => urlParams.setParam('clientStatus', status),
    
    getClientFilter: () => urlParams.getParam('filter') as 'new' | 'returning' | 'at-risk' | '',
    setClientFilter: (filter: string | null) => urlParams.setParam('filter', filter),
    
    getClientSort: () => urlParams.getParam('sort', 'name') as 'name' | 'last-session' | 'created',
    setClientSort: (sort: string) => urlParams.setParam('sort', sort),
    
    getClientTag: () => urlParams.getParam('tag'),
    setClientTag: (tag: string | null) => urlParams.setParam('tag', tag),
    
    // Appointment management
    getAppointmentStatus: () => urlParams.getParam('appointmentStatus') as 'upcoming' | 'past' | 'cancelled' | '',
    setAppointmentStatus: (status: string | null) => urlParams.setParam('appointmentStatus', status),
    
    // Common filters
    getSearch: () => urlParams.getParam('search'),
    setSearch: (search: string | null) => urlParams.setParam('search', search),
    
    getPage: () => urlParams.getNumberParam('page', 1) || 1,
    setPage: (page: number) => urlParams.setParam('page', page > 1 ? page : null),
  };
};

// Portal-specific parameter management
export const usePortalParams = () => {
  const urlParams = useUrlParams();

  return {
    ...urlParams,
    
    // Session management
    getSessionView: () => urlParams.getParam('view', 'upcoming') as 'upcoming' | 'past' | 'all',
    setSessionView: (view: string) => urlParams.setParam('view', view),
    
    getSessionFilter: () => urlParams.getParam('filter') as 'individual' | 'group' | 'workshop' | '',
    setSessionFilter: (filter: string | null) => urlParams.setParam('filter', filter),
    
    getSessionHealer: () => urlParams.getParam('healer'),
    setSessionHealer: (healer: string | null) => urlParams.setParam('healer', healer),
    
    getSessionDate: () => urlParams.getParam('date'),
    setSessionDate: (date: string | null) => urlParams.setParam('date', date),
    
    getSessionStatus: () => urlParams.getParam('status') as 'confirmed' | 'pending' | 'cancelled' | '',
    setSessionStatus: (status: string | null) => urlParams.setParam('status', status),
    
    // Resource management
    getResourceCategory: () => urlParams.getParam('category') as 'meditation' | 'journaling' | 'exercises' | 'videos' | '',
    setResourceCategory: (category: string | null) => urlParams.setParam('category', category),
    
    getResourceAccess: () => urlParams.getParam('access') as 'owned' | 'available' | 'recommended' | '',
    setResourceAccess: (access: string | null) => urlParams.setParam('access', access),
    
    getResourceSort: () => urlParams.getParam('sort', 'newest') as 'newest' | 'popular' | 'alphabetical',
    setResourceSort: (sort: string) => urlParams.setParam('sort', sort),
  };
};